import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EastIcon from '@mui/icons-material/East';
import EditIcon from '@mui/icons-material/Edit';
import { Box, CardActionArea, CircularProgress, Collapse, Grid, IconButton, Paper, TableCell, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import moment from 'moment/moment';
import { darken } from 'polished';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import CalendarIcon from '../../icons/CalendarIcon';
import CalendarNoEndIcon from '../../icons/CalendarNoEndIcon';
import DateTimeIcon from '../../icons/DateTimeIcon';
import PlayIcon from '../../icons/PlayIcon';
import TimeIcon from '../../icons/TimeIcon';
import { MessageContext } from '../../utils/MessageContext';
import { UserContext } from '../../utils/UserContext';
import { useAxios } from '../../utils/useAxios';
import SoundDialog from '../SoundDialog';

export default function MobileListItem({ item, index, drawer }) {

    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [width, setWidth] = useState(window.innerWidth * .85)
    const [type, setType] = useState({
        "representation": false,
        "virtualRepresentation": false,
        "endDateTime": true,
        "endDate": false,
        "timeOnly": false
    });

    const { user, setUser } = useContext(UserContext);
    const { messages } = useContext(MessageContext);

    const soundRef = useRef();

    const axiosInstance = useAxios();

    const { t } = useTranslation();

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth * .85);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
        //eslint-disable-next-line
    }, [])


    const isActive = (start, end) => {
        if (moment(start).isBefore(moment()) && !moment(end).isBefore(moment())) {
            return true;
        }
        return false;
    };

    const isAboveOneDay = (start, end) => {
        const diff = moment.duration(moment(end).diff(moment(start)));
        //handle no enddate
        if (!end) {
            return true;
        }
        if (diff.asHours() > 23) {
            return true;
        } else {
            return false;
        }
    }

    const getMessage = (companyMessageUid) => {
        const msg = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];
        const newType = {
            representation: msg?.proxyMandatory,
            virtualRepresentation: msg?.virtualRepresentation,
            endDateTime: msg?.endDateTimeMandatory,
            endDate: msg?.endDateMandatory,
            timeOnly: msg?.timeOnlyMandatory
        };
        if (!_.isEqual(type, newType)) {
            setType({
                representation: msg?.proxyMandatory,
                virtualRepresentation: msg?.virtualRepresentation,
                endDateTime: msg?.endDateTimeMandatory,
                endDate: msg?.endDateMandatory,
                timeOnly: msg?.timeOnlyMandatory
            });
        };
        return msg;
    };

    const getTitle = (companyMessageUid) => {
        const message = getMessage(companyMessageUid);

        if (message?.timeOnlyMandatory) {
            return t("time-announcement");
        }
        if (message?.endDateTimeMandatory) {
            return t("datetime-announcement");
        }
        if (message?.endDateMandatory) {
            return t("date-announcement")
        }
        if (message?.timeOnlyMandatory === false && message?.endDateTimeMandatory === false && message?.endDateMandatory === false) {
            return t("without-enddate")
        }
        return null;
    };

    const getIcon = (companyMessageUid, item, color) => {
        const message = getMessage(companyMessageUid);

        if (message && !message.endDateMandatory && !message.endDateTimeMandatory && !message.timeOnlyMandatory) {
            return <CalendarNoEndIcon color={color} />
        }
        if (message && message?.endDateTimeMandatory) {
            return <DateTimeIcon color={color} bgColor={isActive(item?.startDate, item?.endDate) ? theme.palette.mode === "dark" ? theme.palette.success.dark : theme.palette.success.light : theme.palette.background.paper} />
        }
        if ((item && isAboveOneDay(item?.startDate, item?.endDate)) || (message && message?.endDateMandatory)) {
            return <CalendarIcon color={color} />
        }
        return <TimeIcon color={color} />
    };

    const getUntilOrSinceDate = (start, end) => {
        const roundToNearestWholeDay = Math.ceil(moment(start).diff(moment(), "days", true));

        if (moment(start).isSame(moment(), "day")) {
            return t("today");
        }
        if (isActive(start, end)) {
            if (roundToNearestWholeDay === 0) {
                return t("since-today")
            }
            if (roundToNearestWholeDay === -1) {
                return t("since-yesterday");
            }
            return t("since-x-days", { x: Math.abs(roundToNearestWholeDay) })
        }
        if (roundToNearestWholeDay === 1) {
            return t("tomorrow");
        }
        return t("in-x-days", { x: roundToNearestWholeDay })
    };

    const handleDelete = async (i) => {
        const newEntries = [...user.calendarEntries];
        const deleted = newEntries.splice(i, 1);

        await axiosInstance.current({
            url: "/entry",
            method: "DELETE",
            data: deleted
        })
        setUser({ ...user, calendarEntries: newEntries })
    }

    const getSound = async () => {
        setLoading(true);

        let entry = {
            companyMessageUid: item.companyMessageUid,
            startDate: item.startDate,
            endDate: item.endDate
        };

        if (item.representativeEmail !== null) {
            entry = { ...entry, representativeEmail: item.representativeEmail }
        }

        await axiosInstance.current({
            url: "sound-preview",
            method: "POST",
            data: entry
        }).then(res => {
            soundRef.current.open(res.data);
            setLoading(false);
        }).catch(err => {
            console.error(err.response);
            setLoading(false);
        })
    }

    const getDifference = (startDate, endDate, companyMessageUid) => {
        const message = getMessage(companyMessageUid);

        const differenceAsDays = moment(endDate).diff(moment(startDate), "days");
        const differenceAsHours = moment(endDate).diff(moment(startDate), "hours");
        const differenceAsMinutes = moment(endDate).diff(moment(startDate), "minutes");

        if (message?.endDateMandatory === false && message?.endDateTimeMandatory === false && message?.timeOnlyMandatory === false) {
            return t("indefinite")
        }
        if (differenceAsHours > 23 || message?.endDateMandatory || message?.endDateTimeMandatory) {
            const hours = Math.trunc(differenceAsHours - (24 * differenceAsDays));
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (Math.trunc(differenceAsDays) !== 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (Math.trunc(differenceAsDays) === 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (hours !== 0 && minutes === 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            if (hours === 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")}`;
        } else if (differenceAsMinutes > 120) {
            const hours = Math.trunc(differenceAsHours);
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (minutes === 0) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            return `${hours} ${hours === 1 ? t("hour") : t("hours")} ${t("and")} ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        } else {
            const minutes = Math.trunc(differenceAsMinutes);
            return `${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        }
    }

    const formatTimeInformation = (start, end, companyMessageUid) => {
        let formattedStart;
        let formattedEnd;
        let duration;
        if (moment(start).isSame(moment(), "days")) {
            formattedStart = moment(start).format("HH:mm");
        } else {
            formattedStart = moment(start).format("DD.MM.YYYY HH:mm");
        }
        if (!end) {
            return `${formattedStart} - ${t("no-end")}`;
        }
        if (moment(end).isSame(moment(), "days")) {
            formattedEnd = moment(end).format("HH:mm");
        } else {
            formattedEnd = moment(end).format("DD.MM.YYYY HH:mm");
        }
        duration = getDifference(start, end, companyMessageUid);
        return `${formattedStart} - ${formattedEnd} ${duration ? `(${duration})` : ""}`;
    };

    const openListItem = () => {
        setOpen(!open);
    };

    return (
        <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell sx={{ p: 0, border: 0 }}>
                <Paper elevation={theme.palette.mode === "dark" ? 1 : 0} sx={{ borderRadius: 3, mb: 1, border: theme.palette.mode === "dark" ? 0 : 1.5, borderColor: "divider", bgcolor: isActive(item?.startDate, item?.endDate) ? theme.palette.mode === "dark" ? "success.dark" : "success.light" : "background.paper" }}>
                    <Collapse in={open} collapsedSize={70}>
                        <Box height={260}>
                            <CardActionArea sx={{ borderRadius: 3 }}>
                                <Grid container onClick={openListItem} sx={{ width: width, color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", p: 2, pl: 0, borderBottom: open ? 1 : 0, borderBottomColor: "divider", cursor: "pointer", ml: 1 }}>
                                    <Grid item xs={2} sx={{ pt: open ? .8 : 0.5 }}>
                                        {getIcon(item.companyMessageUid, item, isActive(item?.startDate, item?.endDate) ? "#fff" : theme.palette.text.primary)}
                                    </Grid>
                                    <Grid item xs={9.5}>
                                        <Grid container direction="column">
                                            <Typography noWrap sx={{ pl: open && item.optionalDisplayName === undefined ? 4.5 : 1, pt: open && item.optionalDisplayName === undefined ? 1.75 : .5, width: "90%", fontWeight: open ? 700 : 400, fontSize: open && item.optionalDisplayName === undefined ? "16px" : "14px", overflow: "hidden", textOverflow: "ellipsis", fontStyle: "normal", lineHeight: "normal", transform: open && item.optionalDisplayName === undefined ? "scale(1.4)" : "scale(1)", transition: open && item.optionalDisplayName === undefined ? "transform 300ms ease" : "none" }}>{item.optionalDisplayName ? open ? item.optionalDisplayName : `${item.optionalDisplayName} ${getUntilOrSinceDate(item.startDate, item.endDate)}` : getUntilOrSinceDate(item.startDate, item.endDate)}</Typography>
                                            <Typography noWrap sx={{ pl: 1, width: "90%", display: open && item.optionalDisplayName === undefined ? "none" : "inline", color: isActive(item?.startDate, item?.endDate) ? darken(0.12, "#fff") : theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : "text.secondary", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", fontStyle: "normal", lineHeight: "normal" }}>{open && item.optionalDisplayName ? getUntilOrSinceDate(item.startDate, item.endDate) : formatTimeInformation(item.startDate, item.endDate, item.companyMessageUid)}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={.5}>
                                        <Box sx={{ pt: 1.5, pl: 1 }}>
                                            <ArrowBackIosNewIcon sx={{ color: isActive(item.startDate, item.endDate) ? "#fff" : "primary.main", fontSize: 19, transform: open ? "rotate(90deg)" : "rotate(180deg)", transition: `transform 200ms ease` }} />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardActionArea>
                            <Grid container>
                                <Box width="80%" sx={{ pl: 2 }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider", pt: 1.5, pb: 1 }}>
                                        <Typography noWrap sx={{ width: "95%", color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary" }}>{getTitle(item?.companyMessageUid)}</Typography>
                                    </Box>
                                    <Grid container sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
                                        <Grid item xs={4} sx={{ textAlign: "center" }}>
                                            <Typography sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 15 }}>
                                                {moment(item.startDate).format("DD.MM.YYYY HH:mm")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <EastIcon />
                                        </Grid>
                                        <Grid item xs={4} sx={{ textAlign: "center", pr: item.endDate ? 0 : 1 }}>
                                            <Typography sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 15 }} >
                                                {item.endDate ? moment(item.endDate).format("DD.MM.YYYY HH:mm") : t("no-enddate")}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {item.representation ?
                                        <Fragment>
                                            <Typography sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>{t("representation")}</Typography>
                                            <Typography sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 14 }}>{item.representation.length > 28 ? `${item.representation.substring(0, 28)} ...` : item.representation}</Typography>
                                        </Fragment>
                                        :
                                        <Typography sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>{t("no-representation")}</Typography>
                                    }
                                </Box>
                                <Box
                                    width="20%"
                                    height={165}
                                    visibility={open ? "visible" : "hidden"}
                                    sx={{
                                        color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary",
                                        borderLeft: 1,
                                        borderColor: "divider",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}
                                >
                                    <Grid container direction="column">
                                        <Tooltip title={t("edit")} placement="right">
                                            <IconButton onClick={() => { drawer(item) }} sx={{ mx: 2 }}>
                                                <EditIcon sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        {!loading ?
                                            <Tooltip title={t("test-listen")} placement="right">
                                                <Box
                                                    sx={{
                                                        height: 32,
                                                        my: 3,
                                                        display: "flex",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    <IconButton onClick={() => { getSound(); }} sx={{ p: 1, border: 1.5, borderColor: isActive(item?.startDate, item?.endDate) ? "#fff" : theme.palette.text.primary }}>
                                                        <PlayIcon color={isActive(item?.startDate, item?.endDate) ? "#fff" : theme.palette.text.primary} fontSize={14} />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip> :
                                            <CircularProgress color="inherit" size={26} sx={{ my: 3.33, ml: 3 }} />
                                        }
                                        <Tooltip title={t("delete")} placement="right">
                                            <IconButton onClick={() => { handleDelete(index) }} sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", "&:hover": { color: "error.main" }, mx: 2 }}>
                                                <DeleteIcon sx={{ color: "inherit", fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Box>
                    </Collapse>
                </Paper>
            </TableCell>
            <SoundDialog message={getTitle(item.companyMessageUid)} startDate={item.startDate} endDate={item.endDate} noEnd={!type.endDate && !type.endDateTime && !type.timeOnly} noMinutes={type.endDate} ref={soundRef} />
        </TableRow>
    )
}